import { useEffect, type ReactNode } from 'react';
import { useThemeStore } from '@/stores/useThemeStore';

/**
 * ThemeProvider -- applies theme class to html element and listens
 * for system preference changes.
 *
 * Theme logic:
 * - dark (default): NO class on html (dark is baseline in globals.css)
 * - light: adds 'light' class to html
 * - system: resolves to dark/light based on prefers-color-scheme
 *
 * Also re-exports useTheme hook for convenience (reads from Zustand store).
 */

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const theme = useThemeStore((s) => s.theme);
  const resolvedTheme = useThemeStore((s) => s.resolvedTheme);
  const _setResolvedTheme = useThemeStore((s) => s._setResolvedTheme);

  // Apply html class whenever resolved theme changes
  useEffect(() => {
    const root = document.documentElement;

    if (resolvedTheme === 'light') {
      root.classList.add('light');
    } else {
      root.classList.remove('light');
    }
  }, [resolvedTheme]);

  // Listen for system preference changes when in "system" mode
  useEffect(() => {
    if (theme !== 'system') return;

    const mql = window.matchMedia('(prefers-color-scheme: light)');

    const handler = (e: MediaQueryListEvent) => {
      _setResolvedTheme(e.matches ? 'light' : 'dark');
    };

    // Set initial resolved value
    _setResolvedTheme(mql.matches ? 'light' : 'dark');

    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [theme, _setResolvedTheme]);

  return <>{children}</>;
}

/**
 * useTheme -- convenience hook that returns theme state and actions.
 *
 * Can be used anywhere within ThemeProvider (or even outside, since
 * the store is global via Zustand).
 */
export function useTheme() {
  const theme = useThemeStore((s) => s.theme);
  const resolvedTheme = useThemeStore((s) => s.resolvedTheme);
  const setTheme = useThemeStore((s) => s.setTheme);
  const toggleTheme = useThemeStore((s) => s.toggleTheme);

  return { theme, resolvedTheme, setTheme, toggleTheme };
}
